import React, { useState, useEffect, useRef } from 'react';
import { customerAPI, Customer } from '../../../api/customer';

interface CustomerFilterProps {
  selectedCustomer: Customer | null;
  onCustomerChange: (customer: Customer | null) => void;
  isFiltering?: boolean;
}

const CustomerFilter: React.FC<CustomerFilterProps> = ({
  selectedCustomer,
  onCustomerChange,
  isFiltering = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await customerAPI.searchCustomers({
          searchQuery: searchQuery.trim(),
          status: 'active',
          page: 0,
          perPage: 10,
        });
        setSuggestions(response.data);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error searching customers:', err);
        setError('Failed to search customers');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  const handleSelectCustomer = (customer: Customer) => {
    setSearchQuery(`${customer.firstName} ${customer.lastName}`);
    onCustomerChange(customer);
    setShowSuggestions(false);
  };

  const handleClearCustomer = () => {
    setSearchQuery('');
    onCustomerChange(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (selectedCustomer) {
      onCustomerChange(null);
    }
  };

  return (
    <div className="flex items-center gap-3" ref={wrapperRef}>
      <label htmlFor="customer-filter" className="text-sm font-medium text-gray-700">
        Customer:
      </label>
      <div className="relative">
        <input
          id="customer-filter"
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          placeholder="Search customers..."
          disabled={isFiltering}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[250px] pr-10"
        />
        {(loading || isFiltering) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((customer) => (
              <div
                key={customer._id}
                onClick={() => handleSelectCustomer(customer)}
                className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-sm text-gray-900">
                  {customer.firstName} {customer.lastName}
                </div>
                <div className="text-xs text-gray-600">{customer.email}</div>
              </div>
            ))}
          </div>
        )}

        {/* No results message */}
        {showSuggestions && suggestions.length === 0 && !loading && searchQuery.trim() && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg p-4">
            <p className="text-sm text-gray-600">No customers found</p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-red-300 rounded-lg shadow-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      {selectedCustomer && !isFiltering && (
        <button
          onClick={handleClearCustomer}
          className="text-sm text-gray-600 hover:text-gray-800 underline"
        >
          Clear
        </button>
      )}
      {isFiltering && (
        <span className="text-sm text-blue-600 font-medium">Filtering...</span>
      )}
    </div>
  );
};

export default CustomerFilter;
